import React from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterSelect from '../../../shared/FilterSelect';
import { Button } from '@mui/material';
import { Badge, Label } from 'flowbite-react';
import Select, { SingleValue } from 'react-select';

export type DriveCandidateSortValue = 'name_asc' | 'name_desc' | 'registered_asc' | 'registered_desc';

export const DRIVE_CANDIDATE_SORT_OPTIONS: { label: string; value: DriveCandidateSortValue }[] = [
	{ label: 'Drive registered (newest first)', value: 'registered_desc' },
	{ label: 'Drive registered (oldest first)', value: 'registered_asc' },
	{ label: 'Name (A–Z)', value: 'name_asc' },
	{ label: 'Name (Z–A)', value: 'name_desc' },
	// { label: 'Default (by score / evaluation level)', value: 'default' }, // not used; restore + backend level sort if needed
];

const parseSortParam = (raw: string | null): DriveCandidateSortValue => {
	if (
		raw === 'name_asc' ||
		raw === 'name_desc' ||
		raw === 'registered_asc' ||
		raw === 'registered_desc'
	) {
		return raw;
	}
	return 'registered_desc';
};

/**
 * Drive registered candidates list sort — matches other FilterSelect chips (Offer Status, etc.).
 */
const DriveCandidateSortFilterInput = ({ showSelect }: { showSelect?: boolean }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const raw = searchParams.get('driveCandidateSort');
	const currentValue = parseSortParam(raw);
	const currentOption =
		DRIVE_CANDIDATE_SORT_OPTIONS.find(o => o.value === currentValue) || DRIVE_CANDIDATE_SORT_OPTIONS[0];

	return (
		<>
			{!showSelect ? (
				<FilterSelect
					arrow={false}
					active={raw !== null}
					placeholder={
						<span className="truncate block max-w-[210px]" title={currentOption.label}>
							{currentOption.label}
						</span>
					}
					onClose={() => {}}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
				>
					{({ handlePopoverClose }) => (
						<div className="min-w-[255px] bg-white dark:bg-[#1F2A37] py-2 ml-auto">
							{DRIVE_CANDIDATE_SORT_OPTIONS.map((option, i) => {
								const selected = option.value === currentValue;
								const isNewestFirst = option.value === 'registered_desc';
								return (
									<Button
										key={i}
										sx={{
											textAlign: 'left',
											display: 'block',
											py: '8px',
											width: '100%',
											px: '18px',
											fontSize: '14px',
											fontWeight: '400',
											color: 'inherit',
											borderRadius: '0',
											textTransform: 'none',
											'&:hover': { backgroundColor: '#7777e73f' },
											backgroundColor: selected ? '#7777e71b' : 'transparent',
										}}
										onClick={() => {
											const next = new URLSearchParams(searchParams);
											if (isNewestFirst) {
												next.delete('driveCandidateSort');
											} else {
												next.set('driveCandidateSort', option.value);
											}
											setSearchParams(next);
											handlePopoverClose?.();
										}}
									>
										<span>{option.label}</span>
										{selected ? (
											<Badge
												className="rounded-[6px] font-[500] text-xs inline-flex w-fit ml-1"
												size="xs"
												color="dark"
											>
												Selected
											</Badge>
										) : null}
									</Button>
								);
							})}
						</div>
					)}
				</FilterSelect>
			) : (
				<div>
					<Label className="mb-1 inline-block">Sort by:</Label>
					<Select
						isSearchable={false}
						menuPosition="fixed"
						placeholder="Sort"
						className="flowbite-select--md"
						classNamePrefix="flowbite-select"
						options={DRIVE_CANDIDATE_SORT_OPTIONS}
						value={currentOption}
						onChange={(opt: SingleValue<(typeof DRIVE_CANDIDATE_SORT_OPTIONS)[number]>) => {
							const v = opt?.value ?? 'registered_desc';
							const next = new URLSearchParams(searchParams);
							if (v === 'registered_desc') {
								next.delete('driveCandidateSort');
							} else {
								next.set('driveCandidateSort', v);
							}
							setSearchParams(next);
						}}
					/>
				</div>
			)}
		</>
	);
};

export default DriveCandidateSortFilterInput;
